import React from 'react';
import { withRouter } from 'react-router';
import { Card, Table, Form, Row, Col, Tooltip } from 'antd';
import { crudService, routerService } from 'core/services';
import { LoginConfiguration } from 'models/LoginConfiguration';
import { LoginConfigurationFilter } from 'models/LoginConfigurationFilter';
import { ColumnProps } from 'antd/lib/table';
import { generalLanguageKeys, generalColumnWidths } from 'config/consts';
import {
  renderMasterIndex,
  getOrderTypeForTable,
} from 'helpers/ant-design/table';
import { tableService } from 'services';
import { useTranslation } from 'react-i18next';
import nameof from 'ts-nameof.macro';
import CollapsibleCard from 'components/CollapsibleCard/CollapsibleCard';
import AdvancedStringFilter from 'components/AdvancedStringFilter/AdvancedStringFilter';
import { API_LOGIN_CONFIGURATION_ROUTE } from 'config/api-consts';
import { loginConfigurationRepository } from '../LoginConfigurationRepository';
import { LOGIN_CONFIGURATION_DETAIL_ROUTE } from 'config/route-consts';
import { Status } from 'models/Status';
import { Helmet } from 'react-helmet';

const { Item: FormItem } = Form;

function LoginConfigurationMaster() {
  const [translate] = useTranslation();
  const { validAction } = crudService.useAction(
    'login-configuration',
    API_LOGIN_CONFIGURATION_ROUTE,
    'portal',
  );
  const [
    filter,
    setFilter,
    list,
    ,
    loading,
    ,
    total,
    ,
    ,
    previewModel,
    ,
    ,
    handleFilter,
    handleSearch,
    handleReset,
    ,
    ,
    ,
  ] = crudService.useMaster<LoginConfiguration, LoginConfigurationFilter>(
    LoginConfiguration,
    LoginConfigurationFilter,
    loginConfigurationRepository.count,
    loginConfigurationRepository.list,
    loginConfigurationRepository.get,
  );

  const [pagination, sorter, handleTableChange] = tableService.useMasterTable(
    filter,
    setFilter,
    total,
    handleSearch,
  );

  const [, handleGoDetail] = routerService.useMasterNavigation(
    LOGIN_CONFIGURATION_DETAIL_ROUTE,
  );

  // const handleChangeStatus = React.useCallback(
  //   params => {
  //     return () => {
  //       params[1].isDisplay = !params[1].isDisplay;
  //       loginConfigurationRepository.update(params[1]).then(res => {
  //         if (res) {
  //           handleDefaultSearch();
  //         }
  //       });
  //     };
  //   },
  //   [handleDefaultSearch],
  // );

  const columns: ColumnProps<LoginConfiguration>[] = React.useMemo(() => {
    return [
      {
        title: translate(generalLanguageKeys.columns.index),
        key: nameof(generalLanguageKeys.index),
        width: generalColumnWidths.index,
        render: renderMasterIndex<LoginConfiguration>(pagination),
      },
      {
        title: translate('loginConfigurations.code'),
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        sorter: true,
        ellipsis: true,
        sortOrder: getOrderTypeForTable<LoginConfiguration>(
          nameof(list[0].code),
          sorter,
        ),
      },
      {
        title: translate('loginConfigurations.name'),
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        ellipsis: true,
        sorter: true,
        sortOrder: getOrderTypeForTable<LoginConfiguration>(
          nameof(list[0].name),
          sorter,
        ),
      },

      {
        title: translate('loginConfigurations.status'),
        key: nameof(list[0].status),
        dataIndex: nameof(list[0].status),
        sorter: true,
        align: 'center',
        sortOrder: getOrderTypeForTable<LoginConfiguration>(
          nameof(list[0].status),
          sorter,
        ),
        render(status: Status) {
          return (
            <div className={status?.id === 1 ? 'active' : ''}>
              <i className="fa fa-check-circle d-flex justify-content-center"></i>
            </div>
          );
        },
      },
      {
        title: translate(generalLanguageKeys.actions.label),
        key: nameof(generalLanguageKeys.columns.actions),
        dataIndex: nameof(list[0].id),
        width: generalColumnWidths.actions,
        align: 'center',
        render(id: number) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              {validAction('update') && (
                <Tooltip title={translate('loginConfigurations.tooltip.edit')}>
                  <button
                    className="btn btn-sm btn-link"
                    onClick={handleGoDetail(id)}
                  >
                    <i className="tio-edit" />
                  </button>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ];
  }, [handleGoDetail, list, pagination, sorter, translate, validAction]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${translate('documentTitle.loginConfiguration')} - eProcurement Management`}</title>
      </Helmet>
      <div className="page master-page">
        <Card title={translate('loginConfigurations.master.title')}>
          <CollapsibleCard
            className="head-borderless mb-3"
            title={translate(generalLanguageKeys.actions.search)}
          >
            <Form>
              <Row>
                <Col className="pl-1" span={6}>
                  <FormItem
                    className="mb-1"
                    label={translate('loginConfigurations.code')}
                    labelAlign="left"
                  >
                    <AdvancedStringFilter
                      filterType={nameof(filter.code.contain)}
                      filter={filter.code}
                      onChange={handleFilter(nameof(filter.code))}
                      className="w-100"
                      placeholder={translate(
                        'loginConfigurations.placeholder.code',
                      )}
                    />
                  </FormItem>
                </Col>
                <Col className="pl-1" span={6}>
                  <FormItem
                    className="mb-1"
                    label={translate('loginConfigurations.name')}
                    labelAlign="left"
                  >
                    <AdvancedStringFilter
                      filterType={nameof(filter.name.contain)}
                      filter={filter.name}
                      onChange={handleFilter(nameof(filter.name))}
                      className="w-100"
                      placeholder={translate(
                        'loginConfigurations.placeholder.name',
                      )}
                    />
                  </FormItem>
                </Col>
              </Row>
            </Form>
            <div className="d-flex justify-content-start mt-3 mb-3 btn-filter">
              {validAction('list') && (
                <>
                  <button
                    className="btn btn-sm btn-primary mr-2"
                    onClick={handleSearch}
                  >
                    <i className="tio-filter_outlined mr-2" />
                    {translate(generalLanguageKeys.actions.filter)}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleReset}
                  >
                    <i className="tio-clear_circle_outlined mr-2" />
                    {translate(generalLanguageKeys.actions.reset)}
                  </button>
                </>
              )}
            </div>
          </CollapsibleCard>
          <Table
            dataSource={list}
            columns={columns}
            size="small"
            tableLayout="fixed"
            loading={loading}
            rowKey={nameof(previewModel.id)}
            pagination={pagination}
            onChange={handleTableChange}
            title={() => (
              <>
                <div className="d-flex justify-content-between">
                  <div className="flex-shrink-1 d-flex align-items-center">
                    {/* <button
                      className="btn btn-sm btn-primary mr-2"
                      onClick={handleCreate}
                    >
                      <i className="fa mr-2 fa-plus" />
                      {translate(generalLanguageKeys.actions.create)}
                    </button>
                    <button
                      className="btn btn-sm btn-danger mr-2"
                      disabled={!hasSelected}
                      onClick={handleBulkDelete}
                    >
                      <i className="fa mr-2 fa-trash" />
                      {translate(generalLanguageKeys.actions.delete)}
                    </button> */}
                  </div>
                  <div className="flex-shrink-1 d-flex align-items-center">
                    {translate('general.master.pagination', {
                      pageSize: pagination.pageSize,
                      total,
                    })}
                  </div>
                </div>
              </>
            )}
          />
        </Card>
      </div>
    </>
  );
}

export default withRouter(LoginConfigurationMaster);
